import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Navbar,
  Footer,
  LectureProfileCard,
  Header,
} from "../components";
import ErrorPage from "./ErrorPage";
import customAxios from "../utils/customAxios";

import { styles } from "../styles";

const StrukturUniversitasPage = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["leaders"],

    queryFn: async () => {
      const response = await customAxios.get("leaders");

      return response?.data?.data || null;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  if (isError) {
    return (
      <ErrorPage
        code="500"
        titleKey="error.500_title"
        defaultTitle="Gagal Memuat Data"
        subtitleKey="error.500_subtitle"
        defaultSubtitle="Terjadi masalah saat terhubung ke server kami. Silakan coba lagi nanti atau kembali ke"
      />
    );
  }

  console.log("" || "a")

  const renderContent = () => {
    if (isLoading) {
      return <div className="text-center text-neutral-60">Memuat data...</div>;
    }

    if (!data || data.length === 0) {
      return (
        <div className="text-center text-neutral-60">
          Data pimpinan belum tersedia.
        </div>
      );
    }

    // 1. Group the flat array into categories based on the position name
    const groupedData = data.reduce((acc, curr) => {
      const key = curr.position_name_id;
      if (!acc[key]) {
        acc[key] = {
          title_id: curr.position_name_id,
          title_en: curr.position_name_en,
          items: [],
          minSortOrder: curr.sort_order,
        };
      }
      acc[key].items.push(curr);

      // Ensure the group's sort order is based on its highest-ranking member
      acc[key].minSortOrder = Math.min(acc[key].minSortOrder, curr.sort_order);
      return acc;
    }, {});

    // 2. Sort the groups by their minSortOrder
    const sortedGroups = Object.values(groupedData).sort(
      (a, b) => a.minSortOrder - b.minSortOrder,
    );

    // 3. Split the array to maintain the UI structure (divider after the first two groups)
    const topLevelGroups = sortedGroups.slice(0, 2); // Typically Rektor & Wakil Rektor
    const remainingGroups = sortedGroups.slice(2); // Dekan, Kaprodi, etc.

    return (
      <section className="flex flex-col justify-center items-center gap-[80px] md:gap-[40px] sm:gap-[8px] w-full">
        {/* --- TOP SECTION (Rektor & Wakil Rektor) --- */}
        <div className="w-full">
          {topLevelGroups.map((group, index) => (
            <div key={group.title_id}>
              <h1
                className={`text-[48px] font-medium text-primary-40 text-center mb-10 md:text-[36px] md:mb-4 sm:text-[24px] sm:mb-8 sm:mt-4 ${
                  index === 0 ? "" : "mt-20"
                }`}
              >
                {currentLanguage === "en" ? group.title_en : group.title_id}
              </h1>
              <div className="w-full flex flex-wrap items-start justify-center gap-[160px] md:gap-[32px] sm:gap-0">
                {group.items
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((lec, idx) => (
                    <LectureProfileCard
                      key={idx}
                      name={lec.custom_name || lec.lecturer_name}
                      role={
                        currentLanguage === "en"
                          ? lec.leadership_field_en
                          : lec.leadership_field_id
                      }
                      image={lec.custom_image || lec.lecturer_image}
                      gender={lec.lecturer_gender}
                    />
                  ))}
              </div>
            </div>
          ))}
        </div>

        {/* --- DIVIDER --- */}
        {remainingGroups.length > 0 && (
          <div className="border-b border-primary-40 w-[160px]"></div>
        )}

        {/* --- REMAINING SECTION (Dekan, Kaprodi, dll) --- */}
        {remainingGroups.length > 0 && (
          <div className="w-full">
            {remainingGroups.map((group) => (
              <div key={group.title_id}>
                <h1 className="text-[48px] font-medium text-primary-40 text-center mb-10 mt-20 md:text-[36px] md:mb-4 sm:text-[24px] sm:mb-8 sm:mt-4">
                  {currentLanguage === "en" ? group.title_en : group.title_id}
                </h1>
                <div className="w-full flex flex-wrap items-start justify-center gap-[160px] md:gap-[36px] sm:gap-0">
                  {group.items
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((lec, idx) => (
                      <LectureProfileCard
                        key={idx}
                        name={lec.custom_name || lec.lecturer_name}
                        role={
                          currentLanguage === "en"
                            ? lec.leadership_field_en
                            : lec.leadership_field_id
                        }
                        image={lec.custom_image || lec.lecturer_image}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <>
      <Navbar />
      <Header title={t("leaders.header")} />
      <main
        className={`${styles.paddingX} py-20 min-h-[600px] grid place-items-center gap-10`}
      >
        <section className="max-w-[1152px] w-full grid place-items-center space-y-20">
          {renderContent()}
        </section>
      </main>
      <Footer />
    </>
  );
};

export default StrukturUniversitasPage;
